import { BaseAuthStore, AuthModel } from 'pocketbase';

interface AuthData {
    token: string;
    model: AuthModel | null;
}

export class AsyncStorageAuthStore extends BaseAuthStore {
    private readonly storageKey: string = '@pocketbase_auth';
    private isInitialized: boolean = false;
    private initializationPromise: Promise<void>;

    constructor(storageKey: string = '@pocketbase_auth') {
        super();

        this.storageKey = storageKey;

        // Load existing auth data on initialization
        this.initializationPromise = this.loadFromStorage();
    }

    private async loadFromStorage(): Promise<void> {
        if (this.isInitialized) return;

        try {
            // Check if we're in a browser environment and localStorage is available
            if (typeof window !== 'undefined' && window.localStorage) {
                const authDataString = localStorage.getItem(this.storageKey);
                if (authDataString) {
                    const authData: AuthData = JSON.parse(authDataString);
                    super.save(authData.token, authData.model);
                }
            }
        } catch (error) {
            console.warn('Failed to load auth data from localStorage:', error);
        } finally {
            this.isInitialized = true;
        }
    }

    public get isRestored(): Promise<void> {
        return this.initializationPromise;
    }

    public async save(token: string, model?: AuthModel): Promise<void> {
        // Ensure initialization is complete
        if (!this.isInitialized) {
            await this.loadFromStorage();
        }

        // Save to memory first
        super.save(token, model);

        // Then persist to localStorage
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                const authData: AuthData = { token, model: model || null };
                localStorage.setItem(this.storageKey, JSON.stringify(authData));
            }
        } catch (error) {
            console.error('Failed to save auth data to localStorage:', error);
        }
    }

    public async clear(): Promise<void> {
        // Clear from memory
        super.clear();

        // Clear from localStorage
        try {
            if (typeof window !== 'undefined' && window.localStorage) {
                localStorage.removeItem(this.storageKey);
            }
        } catch (error) {
            console.warn('Failed to clear auth data from localStorage:', error);
        }
    }
}